import React, { useState} from 'react';
import { useTranslation } from 'react-i18next';
import {useDispatch} from 'react-redux';
import { TABLES } from '../../../../../Apis/Tables';
import TableFunction from '../../../../../components/table/TableFunction';
import {colors} from '../../../../../enums/colors';
import { VIEW_TYPE } from '../../../../../enums/ViewType';
import { SearchByFilters, SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import getTimezoneOffset from '../../../../../utils/GetTimeZone';
import GetDateFilter from '../../../Company/Payroll/Functionality/GetDateFilter';
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomChip from "../../../../../components/CustomChip";
import {ATTENDANCE_MODES, getAttendanceColorByStatus, MODEL_COLOR} from "../Models/Model";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    formatMinutesToHoursMinutes,
    getDateFormat
} from "../../../../../Apis/functions";
import {getAccessMethodById} from "../../../../../enums/accessMethodEnum";
import AccessMethodIcon from "../../../../../components/icons/AccessMethodIcon";
import CustomTooltip from "../../../../../components/CustomTooltip";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {IconAvatarTable} from "../../../../../components/buttons/IconButtons";
import {userType} from "../../../../../enums/userTypeEnum";
import {
    CRITERIA_MODEL,
    DEFAULT_LEFT,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";

/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Software Developer
 * @Email : kpozos@ibl.mx
 *
 */
const DiningAttendanceDayTableApp = ({
    setCriteria,
    setBodyForExport,
    finalArray,
    searchElement,
    selectedOption,
    color,
    modified,
    setModified,
    prevIsPeriod
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const tableId = "tbl_dad_attendance";
    const searchId = "tbl_dad_search_attendance";
    const { getAttendanceDate } = GetDateFilter();
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const option = `${TABLES.FOODIE}`;
    const leftPosition = DEFAULT_LEFT;

    const formatAttendanceDate = (date) => {
        if (!date) return t('no_record');
        return getDateFormat(date, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds12);
    };

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t("name")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("entryAccessMethod")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("entry_time")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("exitAccessMethod")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("exit_time")}</CustomTh>,
            <CustomTh tableId={tableId}/>,
            <CustomTh tableId={tableId}>{t("total_time")}</CustomTh>
        ]
    }

    const getTbody = () => {
        if (!results?.content?.length) return { body: [], data:[] }

        const body = results?.content.map((member, i) => {
            const entry = Array.isArray(member.entries) ? member.entries[0] : null
            const entryAccessMethod = getAccessMethodById(entry?.entryAccessMethodId);
            const exitAccessMethod = getAccessMethodById(entry?.exitAccessMethodId);
            const userTypeName = t(userType[member?.userTypeId]) || EMPTY_VALUES.DEFAULT;

            return [
                <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes="first-column" dataText={member.name}>
                    <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"}>
                        <div className={"ml-1"}>
                            <p className={"mb-1"}>{member?.fullName || member?.name}</p>
                            <CustomChip legend={userTypeName}
                                        background={colors.secondary100}
                                        fontColor={colors.secondary700}
                                        borderColor={colors.secondary150}
                                        variant={'large'}
                            />
                        </div>
                    </CustomImageTable>
                </CustomTd>,
                <CustomTd tableId={tableId} dataText={t(entry?.entryAccessMethodName?.toLowerCase())}>
                    <div className="d-flex gap-2">
                        {entryAccessMethod?.icon &&
                            <CustomTooltip text={t(entryAccessMethod.value)}>
                                <p><AccessMethodIcon className={"icon-hover-secondary-500"} AccessMethodId={entry?.entryAccessMethodId}/></p>

                            </CustomTooltip>
                        }
                        {entry?.entryDeviceName}
                    </div>
                </CustomTd>,
                <CustomTd tableId={tableId}>
                        <CustomChip
                            background={getAttendanceColorByStatus(entry?.entryDateStatus, color, MODEL_COLOR)}
                            fontColor={entry?.entryDate  ? colors.general00 : colors.shades700 }
                            legend={formatAttendanceDate(entry?.entryDate)}
                        />
                </CustomTd>,
                <CustomTd tableId={tableId} dataText={t(entry?.exitAccessMethodName?.toLowerCase())}>
                    <div className='d-flex gap-2'>
                        {exitAccessMethod?.icon && (
                            <CustomTooltip text={t(exitAccessMethod.value)}>
                                <p>
                                    <AccessMethodIcon
                                        className={'icon-hover-secondary-500'}
                                        AccessMethodId={entry?.exitAccessMethodId}
                                    />
                                </p>
                            </CustomTooltip>
                        )}
                        {entry?.exitDeviceName || t('no_record')}
                    </div>
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(entry?.exitDateStatus, color, MODEL_COLOR)}
                        fontColor={entry?.exitDate  ? colors.general00 : colors.shades700 }
                        legend={formatAttendanceDate(entry?.exitDate)}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>=</CustomTd>,
                <CustomTd tableId={tableId}>
                        <CustomChip
                            background={getAttendanceColorByStatus(entry?.totalTimeStatus, color, MODEL_COLOR)}
                            fontColor={entry?.totalTime  ? colors.general00 : colors.shades700 }
                            legend={formatMinutesToHoursMinutes(entry?.totalTime) || t("no_record")}
                            className='w-155 text-nowrap'
                        />
                </CustomTd>,
            ];
        });

        return {
            body,
            data:results
        }
    }

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        //Change from period to daily - NO EXECUTE with simple search
        if(searchElement !== "" && prevIsPeriod.current === true){
            prevIsPeriod.current = false;
            return;
        }

        if(finalArray.length > 0) return;

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate,
            attendanceEndDate,
            attendanceSearchType: ATTENDANCE_MODES.daily,
            key: searchElement,
            model: option
        });

        setBodyForExport(body);

        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setResults(data);
            }
        });
    };

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });

        const body = SEARCH_MODEL.setObject({
             page,
            size: rowsPerPage,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate,
            attendanceEndDate,
            attendanceSearchType: selectedOption,
            criteria: criteriaList,
            model: option,
        });
        setCriteria(body);
        dispatch(SearchByFilters({ option, body })).then(({ payload: { data: { data } } }) => {
            setResults(data);
        });
    }

    const {thead, tbody, pagination, setPage, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        hasContainer:true,
        searchElement, //New
        getData: getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray//New - apply only with advanced search
    });

    return {
        option,
        setPage,
        rowsPerPage,
        attendanceStartDate,
        attendanceEndDate,
        results,
        setResults,
        selectedDate,
        setSelectedDate,
        pagination,
        tableId,
        searchId,
        thead,
        tbody
    };
};

export default DiningAttendanceDayTableApp;
